import React, { useState, useEffect, useCallback, useRef } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { Col, Row, Modal, ModalBody, Card, Alert } from "reactstrap";
import { Table, Empty, Badge, Select, Tag } from "antd";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import moment from "moment";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";
import misc from "../../../api/misc/miscService";
import type {
  CouponDetail,
  CouponApp,
  CouponLinkedCustomer,
  Country,
  State,
  AppDropdownItem,
} from "../../../types/super-admin";

const usageTypeOptions = [
  { value: "Single-use", label: "Single-use" },
  { value: "Multi-use", label: "Multi-use" },
];

const CouponDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const formMethods = useForm({ mode: "onChange" });
  const { handleSubmit, setValue, reset, watch, formState: { dirtyFields } } = formMethods;

  const [coupon, setCoupon] = useState<CouponDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [] as string[],
  });

  // Edit modal state for apps, countries, states
  const [editAppIds, setEditAppIds] = useState<number[]>([]);
  const [appOptions, setAppOptions] = useState<{ value: number; label: string }[]>([]);
  const [countries, setCountries] = useState<{ value: any; label: string }[]>([]);
  const [states, setStates] = useState<{ value: any; label: string }[]>([]);

  const selectedCountry = watch("countryId");
  const selectedValidFrom = watch("validFrom");

  const fetchCouponDetails = useCallback(async () => {
    try {
      setLoading(true);
      const res = await superAdminService.getCouponById(id);
      if (res.status && res.data) {
        setCoupon(res.data);
      }
    } catch (error: any) {
      console.error("Failed to fetch coupon:", error.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchCouponDetails();
  }, [fetchCouponDetails]);

  const handleActivate = async () => {
    try {
      const res = await superAdminService.activateCoupon(id);
      if (res.status) {
        Swal.fire("Activated!", "Coupon has been activated.", "success");
        fetchCouponDetails();
      } else {
        Swal.fire("Error", res.message || "Failed to activate", "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    }
  };

  const handleDeactivate = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will deactivate the coupon.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, deactivate!",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await superAdminService.deactivateCoupon(id);
          if (res.status) {
            Swal.fire("Deactivated!", "Coupon has been deactivated.", "success");
            fetchCouponDetails();
          } else {
            Swal.fire("Error", res.message || "Failed to deactivate", "error");
          }
        } catch (error: any) {
          Swal.fire("Error", error.message, "error");
        }
      }
    });
  };

  const handleUnlinkCustomer = async (customerId: number) => {
    if (!coupon) return;
    Swal.fire({
      title: "Unlink Customer?",
      text: "This will remove the coupon from this customer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, unlink!",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await superAdminService.unlinkCouponFromCustomer({
            customerId,
            couponId: coupon.couponId,
          });
          if (res.status) {
            Swal.fire("Unlinked!", "Customer has been unlinked.", "success");
            fetchCouponDetails();
          } else {
            Swal.fire("Error", res.message || "Failed to unlink", "error");
          }
        } catch (error: any) {
          Swal.fire("Error", error.message, "error");
        }
      }
    });
  };

  const fetchEditDropdowns = useCallback(async () => {
    const results = await Promise.allSettled([
      superAdminService.getAppDropdown(),
      misc.getCountries(),
    ]);

    if (results[0].status === "fulfilled") {
      const appRes = results[0].value;
      if (appRes.status && appRes.data) {
        const apps: AppDropdownItem[] = Array.isArray(appRes.data) ? appRes.data : [];
        setAppOptions(apps.map((app) => ({ value: app.appId, label: app.appName })));
      }
    }

    if (results[1].status === "fulfilled") {
      const countriesRes = results[1].value;
      if (countriesRes.status && countriesRes.data) {
        const list: Country[] = Array.isArray(countriesRes.data) ? countriesRes.data : [];
        setCountries(
          list.map((item) => ({ value: item.countryId, label: item.countryName }))
        );
      }
    }
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      const fetchStates = async () => {
        try {
          const statesRes = await misc.getStatesByCountry(Number(selectedCountry));
          if (statesRes.status && statesRes.data) {
            const list: State[] = Array.isArray(statesRes.data) ? statesRes.data : [];
            setStates(list.map((item) => ({ value: item.stateId, label: item.stateName })));
          }
        } catch (error: any) {
          console.error("Failed to fetch states:", error.message);
        }
      };
      fetchStates();
    } else {
      setStates([]);
    }
  }, [selectedCountry]);

  const openEditModal = async () => {
    if (!coupon) return;
    setAlertInfo({ type: "", message: "", errors: [] });
    await fetchEditDropdowns();

    setValue("couponName", coupon.couponName || "");
    setValue("description", coupon.description || "");
    if (coupon.discountType === "Percent") {
      setValue("discountPercent", coupon.discountPercent || coupon.discountValue || "");
    } else {
      setValue("discountValue", coupon.discountValue || "");
    }
    setValue("usageType", coupon.usageType || "");
    setValue("maxRedemptions", coupon.maxRedemptions || "");
    setValue("countryId", coupon.countryId || undefined);
    setValue("stateId", coupon.stateId || undefined);
    setValue("validFrom", coupon.validFrom ? dayjs(coupon.validFrom) : undefined);
    setValue("validTo", coupon.validTo ? dayjs(coupon.validTo) : undefined);
    setEditAppIds(
      coupon.applicableApps?.map((a) => a.appId) || []
    );
    setEditModal(true);
  };

  const submitEdit = async (data: any) => {
    if (!coupon) return;
    setEditLoading(true);
    setAlertInfo({ type: "", message: "", errors: [] });

    try {
      const payload: any = { couponId: coupon.couponId };

      if (dirtyFields.couponName) payload.couponName = data.couponName?.trim();
      if (dirtyFields.description) payload.description = typeof data.description === "string" ? data.description.trim() : data.description;
      if (coupon.discountType === "Percent") {
        if (dirtyFields.discountPercent) payload.discountPercent = Number(data.discountPercent);
      } else {
        if (dirtyFields.discountValue) payload.discountValue = Number(data.discountValue);
      }
      if (dirtyFields.usageType) payload.usageType = data.usageType;
      if (dirtyFields.maxRedemptions) payload.maxRedemptions = Number(data.maxRedemptions);
      if (dirtyFields.countryId) payload.countryId = data.countryId != null ? Number(data.countryId) : null;
      if (dirtyFields.stateId) payload.stateId = data.stateId != null ? Number(data.stateId) : null;
      if (dirtyFields.validFrom) payload.validFrom = new Date(data.validFrom).toLocaleDateString("en-CA");
      if (dirtyFields.validTo) payload.validTo = new Date(data.validTo).toLocaleDateString("en-CA");

      // NOTE: App-wise is hidden for now — no applicableAppIds updates
      // if (coupon.discountScope === "App-wise") {
      //   const origIds = (coupon.applicableApps || []).map((a) => a.appId).sort().join(",");
      //   const newIds = [...editAppIds].sort().join(",");
      //   if (origIds !== newIds) {
      //     payload.applicableAppIds = editAppIds;
      //   }
      // }

      if (Object.keys(payload).length === 1) {
        setAlertInfo({ type: "info", message: "No changes detected.", errors: [] });
        setEditLoading(false);
        return;
      }

      const res = await superAdminService.updateCoupon(payload);
      if (res.status) {
        Swal.fire("Updated!", "Coupon has been updated.", "success");
        setEditModal(false);
        reset();
        fetchCouponDetails();
      } else {
        const errorMsg = Array.isArray(res.message)
          ? res.message.join(", ")
          : res.message || "An error occurred";
        setAlertInfo({ type: "error", message: errorMsg, errors: [] });
      }
    } catch (error: any) {
      setAlertInfo({
        type: "error",
        message: error.message || "An error occurred",
        errors: [],
      });
    } finally {
      setEditLoading(false);
    }
  };

  const customerColumns = [
    {
      title: "Customer Code",
      dataIndex: "customerCode",
      key: "customerCode",
      width: 150,
      ellipsis: true,
      render: (text: string, record: CouponLinkedCustomer) =>
        text ? (
          <Link
            to={`/sa-customers/${record.customerId}/control-panel?tab=coupons`}
            className="link link-primary"
          >
            {text}
          </Link>
        ) : "-",
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      width: 240,
      ellipsis: true,
      render: (text: string, record: CouponLinkedCustomer) =>
        text ? (
          <Link
            to={`/sa-customers/${record.customerId}/control-panel?tab=coupons`}
            className="link link-primary"
          >
            {text}
          </Link>
        ) : "-",
    },
    {
      title: "Assigned At",
      dataIndex: "assignedAt",
      key: "assignedAt",
      width: 140,
      render: (date: string) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 110,
      align: "center" as const,
      render: (isActive: boolean) => (
        <span className={`tb-status text-${isActive ? "success" : "danger"}`}>
          {isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 110,
      align: "center" as const,
      render: (_: any, record: CouponLinkedCustomer) =>
        record.isActive ? (
          <a
            href="#unlink"
            className="link link-danger"
            onClick={(e) => {
              e.preventDefault();
              handleUnlinkCustomer(record.customerId);
            }}
          >
            Unlink
          </a>
        ) : null,
    },
  ];

  if (loading) {
    return (
      <React.Fragment>
        <Head title="Coupon Details" />
        <Content>
          <LoadingSpinner height="300px" />
        </Content>
      </React.Fragment>
    );
  }

  if (!coupon) {
    return (
      <React.Fragment>
        <Head title="Coupon Details" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Coupon not found" />
            <Button
              color="primary"
              className="mt-3"
              onClick={() => navigate("/sa-coupons")}
            >
              Back to List
            </Button>
          </div>
        </Content>
      </React.Fragment>
    );
  }

  const statusColor: Record<string, string> = {
    Active: "green",
    Inactive: "red",
    Expired: "orange",
  };

  return (
    <React.Fragment>
      <Head title="Coupon Details" />
      <Content>
        {/* Header */}
        <BlockHead size="sm">
          <BlockBetween className="g-3">
            <BlockHeadContent>
              <BlockTitle page>
                {coupon.couponCode} — {coupon.couponName}{" "}
                <Badge color={statusColor[coupon.status] || "default"}>
                  {coupon.status}
                </Badge>
              </BlockTitle>
              <BlockDes className="text-soft">
                <ul className="list-inline">
                  <li>
                    Type:{" "}
                    <Badge color={coupon.discountType === "Percent" ? "blue" : "orange"}>
                      {coupon.discountType}
                    </Badge>
                  </li>
                  {/* NOTE: Scope hidden for now
                  <li>
                    Scope: <span className="text-base">{coupon.discountScope}</span>
                  </li>
                  */}
                </ul>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                <li>
                  <Link to="/sa-coupons">
                    <Button outline className="bg-white d-none d-sm-inline-flex">
                      <Icon name="arrow-left"></Icon>
                      <span>Back</span>
                    </Button>
                  </Link>
                </li>
                <li>
                  <Button color="primary" onClick={openEditModal}>
                    <Icon name="edit-alt-fill"></Icon>
                    <span>Edit</span>
                  </Button>
                </li>
                <li>
                  {coupon.status === "Active" ? (
                    <Button color="danger" onClick={handleDeactivate}>
                      <Icon name="na"></Icon>
                      <span>Deactivate</span>
                    </Button>
                  ) : coupon.status === "Inactive" ? (
                    <Button color="success" onClick={handleActivate}>
                      <Icon name="check-circle"></Icon>
                      <span>Activate</span>
                    </Button>
                  ) : null}
                </li>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {/* Coupon Info */}
        <Block>
          <Row className="gy-5">
            <Col lg="6">
              <BlockHead>
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h5">Coupon Information</BlockTitle>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>
              <Card className="card-bordered">
                <ul className="data-list is-compact">
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Coupon Code</div>
                      <div className="data-value">{coupon.couponCode}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Coupon Name</div>
                      <div className="data-value">{coupon.couponName}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Description</div>
                      <div className="data-value">{coupon.description || "-"}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Discount Type</div>
                      <div className="data-value">
                        <Badge color={coupon.discountType === "Percent" ? "blue" : "orange"}>
                          {coupon.discountType}
                        </Badge>
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Discount Value</div>
                      <div className="data-value">
                        {coupon.discountType === "Percent"
                          ? `${coupon.discountPercent || coupon.discountValue}%`
                          : Number(coupon.discountValue).toFixed(2)}
                      </div>
                    </div>
                  </li>
                  {/* NOTE: Scope hidden for now (App-wise not in use)
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Scope</div>
                      <div className="data-value">{coupon.discountScope}</div>
                    </div>
                  </li>
                  */}
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Usage Type</div>
                      <div className="data-value">{coupon.usageType}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Max Redemptions</div>
                      <div className="data-value">
                        {coupon.maxRedemptions || "Unlimited"}
                      </div>
                    </div>
                  </li>
                </ul>
              </Card>
            </Col>
            <Col lg="6">
              <BlockHead>
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h5">Location & Validity</BlockTitle>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>
              <Card className="card-bordered">
                <ul className="data-list is-compact">
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Country</div>
                      <div className="data-value">{coupon.countryName || "Global"}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">State</div>
                      <div className="data-value">{coupon.stateName || "-"}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Valid From</div>
                      <div className="data-value">
                        {moment(coupon.validFrom).format("DD MMM YYYY")}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Valid To</div>
                      <div className="data-value">
                        {moment(coupon.validTo).format("DD MMM YYYY")}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Status</div>
                      <div className="data-value">
                        <span
                          className={`tb-status text-${
                            coupon.status === "Active"
                              ? "success"
                              : coupon.status === "Expired"
                              ? "warning"
                              : "danger"
                          }`}
                        >
                          {coupon.status}
                        </span>
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Created</div>
                      <div className="data-value">
                        {moment.utc(coupon.createdAt).local().format("DD MMM YYYY hh:mm A")}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Updated</div>
                      <div className="data-value">
                        {moment.utc(coupon.updatedAt).local().format("DD MMM YYYY hh:mm A")}
                      </div>
                    </div>
                  </li>
                </ul>
              </Card>
            </Col>
          </Row>
        </Block>

        {/* NOTE: Applicable Applications section hidden for now (App-wise not in use)
        {coupon.discountScope === "App-wise" && (
          <Block>
            <BlockHead size="sm">
              <BlockTitle tag="h5">
                Applicable Applications ({coupon.applicableApps?.length || 0})
              </BlockTitle>
            </BlockHead>
            <Card className="card-bordered">
              <div className="card-inner">
                {coupon.applicableApps && coupon.applicableApps.length > 0 ? (
                  <Flex wrap="wrap" gap="small">
                    {coupon.applicableApps.map((app) => (
                      <Tag
                        key={app.couponAppId}
                        color="geekblue"
                        style={{
                          fontSize: "13.5px",
                          fontWeight: 500,
                          padding: "6px 14px",
                          borderRadius: "6px",
                          lineHeight: "1.4",
                        }}
                      >
                        {app.appName}
                      </Tag>
                    ))}
                  </Flex>
                ) : (
                  <Empty description="No applications specified" />
                )}
              </div>
            </Card>
          </Block>
        )}
        */}

        {/* Linked Customers */}
        <Block>
          <BlockHead size="sm">
            <BlockTitle tag="h5">
              Linked Customers ({coupon.linkedCustomers?.length || 0})
            </BlockTitle>
          </BlockHead>
          <Card className="card-bordered">
            <div className="card-inner">
              {coupon.linkedCustomers && coupon.linkedCustomers.length > 0 ? (
                <Table
                  columns={customerColumns}
                  dataSource={coupon.linkedCustomers}
                  rowKey="customerCouponId"
                  scroll={{ x: "max-content", y: 300 }}
                  pagination={{ pageSize: 10 }}
                  bordered
                  size="middle"
                />
              ) : (
                <Empty description="No customers linked to this coupon" />
              )}
            </div>
          </Card>
        </Block>
      </Content>

      {/* Edit Modal */}
      <Modal
        isOpen={editModal}
        toggle={() => setEditModal(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <ModalBody>
          <a
            href="#cancel"
            onClick={(e) => {
              e.preventDefault();
              setEditModal(false);
              reset();
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="p-2">
            {alertInfo.message && (
              <div className="mb-3 mt-1">
                <Alert
                  color={alertInfo.type === "error" ? "danger" : "success"}
                  className="alert-icon"
                >
                  <strong>{alertInfo.message}</strong>
                  <Icon
                    name={
                      alertInfo.type === "error"
                        ? "alert-circle"
                        : "check-circle"
                    }
                  />
                </Alert>
              </div>
            )}

            <h5 className="title">Edit Coupon</h5>
            <div className="mt-4">
              {/* Non-editable fields shown as read-only info */}
              <Row className="gy-2 mb-3">
                <Col sm="6">
                  <small className="text-soft d-block">Coupon Code</small>
                  <strong>{coupon.couponCode}</strong>
                </Col>
                <Col sm="6">
                  <small className="text-soft d-block">Discount Type</small>
                  <strong>{coupon.discountType}</strong>
                </Col>
                {/* NOTE: Scope hidden for now
                <Col sm="6">
                  <small className="text-soft d-block">Discount Scope</small>
                  <strong>{coupon.discountScope}</strong>
                </Col>
                */}
              </Row>
              <hr className="mt-0 mb-3" />

              <FormProvider {...formMethods}>
                <form
                  className="content clearfix"
                  onSubmit={handleSubmit(submitEdit)}
                >
                  <Row className="gy-4">
                    <Col lg="12">
                      <FormInput
                        name="couponName"
                        label="Coupon Name"
                        type="text"
                        required
                        maxLength={200}
                        placeholder="Enter coupon name"
                      />
                    </Col>
                    <Col lg="12">
                      <FormInput
                        name="description"
                        label="Description"
                        type="text"
                        maxLength={255}
                        placeholder="Enter description"
                      />
                    </Col>
                    {coupon.discountType === "Percent" ? (
                      <Col lg="6">
                        <FormInput
                          name="discountPercent"
                          label="Discount %"
                          type="number"
                          placeholder="1 - 100"
                          customValidation={{
                            validate: (val: any) => {
                              if (!val) return true;
                              const num = Number(val);
                              if (num < 1 || num > 100) return "Must be between 1 and 100";
                              return true;
                            },
                          }}
                        />
                      </Col>
                    ) : (
                      <Col lg="6">
                        <FormInput
                          name="discountValue"
                          label="Discount Amount"
                          type="number"
                          placeholder="Enter amount"
                        />
                      </Col>
                    )}
                    <Col lg="6">
                      <FormSelect
                        name="usageType"
                        label="Usage Type"
                        options={usageTypeOptions}
                        placeholder="-Select Usage Type-"
                      />
                    </Col>
                    <Col lg="6">
                      <FormInput
                        name="maxRedemptions"
                        label="Max Redemptions"
                        type="number"
                        placeholder="Leave empty for unlimited"
                      />
                    </Col>

                    {/* NOTE: App-wise application edit hidden for now
                    {coupon.discountScope === "App-wise" && (
                      <Col lg="12">
                        <div className="form-group">
                          <label className="form-label">Applicable Applications</label>
                          <Select
                            mode="multiple"
                            placeholder="Select applications"
                            style={{ width: "100%" }}
                            showSearch
                            optionFilterProp="label"
                            value={editAppIds}
                            onChange={(val) => setEditAppIds(val)}
                            options={appOptions}
                          />
                        </div>
                      </Col>
                    )}
                    */}

                    <Col lg="6">
                      <FormSelect
                        name="countryId"
                        label="Country"
                        options={countries}
                        placeholder="-Select Country-"
                        showSearch={true}
                      />
                    </Col>
                    <Col lg="6">
                      <FormSelect
                        name="stateId"
                        label="State"
                        options={states}
                        placeholder="-Select State-"
                        disabled={!selectedCountry}
                        showSearch={true}
                      />
                    </Col>
                    <Col lg="6">
                      <FormDatePicker
                        name="validFrom"
                        label="Valid From"
                        placeholder="Select start date"
                        minDate={new Date()}
                      />
                    </Col>
                    <Col lg="6">
                      <FormDatePicker
                        name="validTo"
                        label="Valid To"
                        required
                        placeholder="Select end date"
                        minDate={(() => {
                          // End date must be strictly after today, and not before the start date.
                          const tomorrow = new Date();
                          tomorrow.setHours(0, 0, 0, 0);
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          const vfSource = selectedValidFrom || coupon.validFrom;
                          const vf = vfSource ? new Date(vfSource) : tomorrow;
                          return vf.getTime() > tomorrow.getTime() ? vf : tomorrow;
                        })()}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col lg="12">
                      <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                        <li>
                          <Button color="primary" size="md" type="submit">
                            {editLoading ? <LoadingSpinner size="sm" inline /> : "Save Changes"}
                          </Button>
                        </li>
                        <li>
                          <a
                            href="#cancel"
                            onClick={(e) => {
                              e.preventDefault();
                              if (!editLoading) {
                                setEditModal(false);
                                reset();
                              }
                            }}
                            className={`link link-light${editLoading ? " disabled text-muted" : ""}`}
                            style={editLoading ? { pointerEvents: "none" } : {}}
                          >
                            Cancel
                          </a>
                        </li>
                      </ul>
                    </Col>
                  </Row>
                </form>
              </FormProvider>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default CouponDetails;
